/**
 * Typesense-powered instant search dropdown for the Maximize theme header.
 *
 * Maximize's header search (snippets/header-search.liquid) is Alpine.js
 * driven -- x-data="xModalSearch(...)" -- not a Dawn-style custom element.
 * Rather than monkey-patching xModalSearch (whose source isn't part of this
 * integration and could change independently of this file), this registers
 * a small, self-contained Alpine component -- xTypesenseSearch -- that is
 * nested *inside* xModalSearch in the template. xModalSearch keeps owning
 * the open/close chrome (overlay, focus trap, Escape key, isFocusedSearchForm);
 * this component only owns the query input and the results list.
 *
 * Requires:
 *   window.TypesenseSearchConfig = {
 *     host, port, protocol, apiKey, collection, locale, currency, moneyFormat
 *   }
 *   set by an inline script in sections/header.liquid, plus the Typesense
 *   JS client (typesense-min.js) loaded before this file.
 *
 * Typesense document schema (see typesense/schema.ts):
 *   id, gid, handle, skus, sku, variant_ids, variant_id, image_url,
 *   available, name_en, name_ar, description_en, description_ar,
 *   price, price_max, currency, vendor_handle, vendor_name_en,
 *   vendor_name_ar, vendor_logo_url, collection_handles, collections_en,
 *   collections_ar, collection_images, offer_type, offer_badge,
 *   is_prescribed, updated_at
 *
 * Badge styling mirrors snippets/offer-type-badge-card.liquid (the yellow
 * corner tag) and snippets/prescribed-badge-cart.liquid (the blue pill)
 * exactly, so results look the same whether they came from Shopify's
 * native predictive search or Typesense.
 *
 * NOTE: vendor_logo_url is in schema.ts but was missing from the *live*
 * collection as of the last sync -- re-sync (recreate + re-alias) before
 * expecting logos to actually show up; the code degrades gracefully to the
 * brand name filling the same square if the field comes back empty.
 *
 * Mobile: brands/collections render as a single horizontally-scrolling row
 * (not a 2-column grid) below the lg breakpoint, and the sidebar column is
 * full-width (not a fixed 220px box) while stacked -- fixed widths + a grid
 * only make sense once the layout switches to a side-by-side row at lg.
 */
(function () {
  "use strict";
  
  var ARABIC_CHAR_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  var LATIN_CHAR_RE = /[A-Za-z]/;
  var cache = {};
  var DEFAULT_IMAGE = "https://cdn.shopify.com/s/files/1/0799/6928/8436/files/placeholder-image_430x.png";


  injectPrescribedBadgeStyle();

  // --- Alpine component -----------------------------------------------

  function xTypesenseSearchFactory() {
    return {
      tsQuery: "",
      tsLoading: false,
      tsResultHtml: "",
      _requestId: 0,

      // Called on @input.debounce from the search box in header-search.liquid.
      tsSearch: function (value) {
        var term = (value || "").trim();
        this.tsQuery = term;

        if (!term) {
          this.tsResultHtml = "";
          this.tsLoading = false;
          return;
        }

        var cfg = window.TypesenseSearchConfig;
        if (!cfg || !cfg.apiKey || typeof Typesense === "undefined") {
          console.warn("Typesense search: missing config or client library, skipping.");
          return;
        }

        var isArabic = detectQueryIsArabic(term, cfg);
        var cacheKey = (isArabic ? "ar:" : "en:") + term.toLowerCase();

        if (cache[cacheKey]) {
          this.tsResultHtml = cache[cacheKey];
          this.tsLoading = false;
          return;
        }

        this.tsLoading = true;
        var requestId = ++this._requestId;
        var self = this;

        /*Promise.all([searchProducts(cfg, term, isArabic), searchRefinements(cfg, term, isArabic)])
          .then(function (results) {
            var hits = results[0];
            var refinementHits = results[1];
            if (self._requestId !== requestId) return; // stale response, user kept typing
            var t = strings(isArabic);
            var vendors = collectVendors(refinementHits, isArabic, term);
            var collections = collectCollections(refinementHits, term, isArabic);
            var html = buildResultsMarkup(cfg, term, isArabic, t, hits, vendors, collections);*/
        Promise.all([searchProducts(cfg, term, isArabic), searchRefinements(cfg, term, isArabic)])
          .then(function (results) {
            var hits = results[0];
            var refinements = results[1];
            if (self._requestId !== requestId) return; // stale response, user kept typing
            var t = strings(isArabic);
            var vendors = collectVendors(refinements.vendorHits, isArabic, term);
            var collections = collectCollections(refinements.collectionHits, term, isArabic);
            var html = buildResultsMarkup(cfg, term, isArabic, t, hits, vendors, collections);
            cache[cacheKey] = html;
            self.tsResultHtml = html;
            self.tsLoading = false;
          })
          .catch(function (err) {
            console.error("Typesense search error", err);
            self.tsLoading = false;
            self.tsResultHtml = "";
          });
      },

      // Called on Enter -- sends the shopper to the full search results page.
      tsSubmit: function () {
        if (!this.tsQuery) return;

        var cfg = window.TypesenseSearchConfig || {};
        var isArabic = detectQueryIsArabic(this.tsQuery, cfg);

        var url = localizedUrl(
          "/search?q=" + encodeURIComponent(this.tsQuery) + "&type=product",
          isArabic
        );

        window.location.href = url;
      },
    };
  }

  function register() {
    Alpine.data("xTypesenseSearch", xTypesenseSearchFactory);
  }

  // Works whether this script runs before or after Alpine's own bootstrap.
  if (window.Alpine) {
    register();
  } else {
    document.addEventListener("alpine:init", register);
  }

  // --- Typesense client -------------------------------------------------

  function getClient(cfg) {
    if (!getClient._instance) {
      getClient._instance = new Typesense.Client({
        nodes: [{ host: cfg.host, port: cfg.port, protocol: cfg.protocol }],
        apiKey: cfg.apiKey,
        connectionTimeoutSeconds: 2,
      });
    }
    return getClient._instance;
  }

  // Broader query just to surface distinct brands/collections -- separate
  // from the tightly-scoped product query below so the product grid doesn't
  // get crowded out chasing pill matches.
  function searchRefinements(cfg, q, isArabic) {
    var vendorField = isArabic ? "vendor_name_ar" : "vendor_name_en";
    var collectionField = isArabic ? "collections_ar" : "collections_en";
    
    /*return getClient(cfg)
      .collections(cfg.collection)
      .documents()
      .search({
        q:q,
        query_by: vendorField + "," + collectionField,
        query_by_weights:"5,3",
        num_typos: 2,
        typo_tokens_threshold: 1,
        prefix: true,
        split_join_tokens: true,
        drop_tokens_threshold: 0,
        per_page:20
      })
      .then(function (res) {
        return res.hits || [];
      });*/
    // commented is the previous version - below the new version
    var documents = getClient(cfg).collections(cfg.collection).documents();

    var searchOptions = {
      num_typos: 1, // brand/collection names are short -- 2 typos was too
      // permissive here and could quietly typo-match to an unrelated name.
      prefix: true,
      per_page: 20,
    };

    var vendorSearch = documents.search(
      Object.assign({ q: q, query_by: vendorField }, searchOptions)
    );
    var collectionSearch = documents.search(
      Object.assign({ q: q, query_by: collectionField }, searchOptions)
    );

    return Promise.all([vendorSearch, collectionSearch]).then(function (results) {
      return {
        vendorHits: (results[0] && results[0].hits) || [],
        collectionHits: (results[1] && results[1].hits) || [],
      };
    });
  }

  function searchProducts(cfg, q, isArabic) {
    return getClient(cfg)
      .collections(cfg.collection)
      .documents()
      .search({
        q: q,
        query_by: isArabic
          ? "name_ar,description_ar,vendor_name_ar,collections_ar,sku"
          : "name_en,description_en,vendor_name_en,collections_en,sku",
        query_by_weights: "4,1,3,2,5",
        sort_by: "available:desc,_text_match:desc",
        highlight_full_fields: isArabic ? "name_ar" : "name_en",
        //filter_by: "available:true",
        per_page: 10, // header dropdown, not the full results page -- keep it tight
      })
      .then(function (res) {
        return res.hits || [];
      });
  }

  // --- Locale detection ---------------------------------------------
  // Primary signal is the script of what was actually typed; falls back to
  // page locale only for script-neutral input (digits, SKUs, punctuation).

  function detectQueryIsArabic(term, cfg) {
    if (ARABIC_CHAR_RE.test(term)) return true;
    if (LATIN_CHAR_RE.test(term)) return false;
    return pageLooksArabic(cfg);
  }

  function pageLooksArabic(cfg) {
    if (cfg.locale && cfg.locale.toLowerCase().indexOf("ar") === 0) return true;
    var htmlLang = (document.documentElement.getAttribute("lang") || "").toLowerCase();
    if (htmlLang.indexOf("ar") === 0) return true;
    if ((document.documentElement.getAttribute("dir") || "").toLowerCase() === "rtl") return true;
    return false;
  }

  function strings(isArabic) {
    return isArabic
      ? {
          brands: "العلامات التجارية",
          collections: "التصنيفات",
          products: "المنتجات",
          noResults: "لا توجد نتائج لـ",
          viewAll: 'عرض جميع النتائج لـ "{term}"',
        }
      : {
          brands: "Brands",
          collections: "Collections",
          products: "Products",
          noResults: "No results found for",
          viewAll: 'View all results for "{term}"',
        };
  }

  // --- Client-side dedup ---------------------------------------------

  function collectVendors(hits, isArabic, term, limit) {
    limit = limit || 6;
    var needle = (term || "").trim().toLowerCase();
    var seen = {};
    var out = [];
    for (var i = 0; i < hits.length && out.length < limit; i++) {
      var doc = hits[i].document;
      var name = isArabic ? doc.vendor_name_ar : doc.vendor_name_en;
      if (!name || !doc.vendor_handle || seen[doc.vendor_handle]) continue;
      if (needle && name.toLowerCase().indexOf(needle) === -1) continue;
      seen[doc.vendor_handle] = true;
      out.push({
        handle: doc.vendor_handle,
        name: name,
        urlName: doc.vendor_name_en,
        logoUrl: doc.vendor_logo_url || ""
      });
    }
    return out;
  }

  // collection_handles / collections_en / collections_ar / collection_images
  // are parallel arrays on each product document (same index = same
  // collection), so image lookup just mirrors the name/handle lookup.
  function collectCollections(hits, term, isArabic, limit) {
    limit = limit || 6;
    var needle = term.trim().toLowerCase();
    var seen = {};
    var out = [];
    for (var i = 0; i < hits.length && out.length < limit; i++) {
      var doc = hits[i].document;
      var handles = doc.collection_handles || [];
      var names = (isArabic ? doc.collections_ar : doc.collections_en) || [];
      var images = doc.collection_images || [];
      for (var j = 0; j < names.length && out.length < limit; j++) {
        var name = names[j];
        var handle = handles[j];
        if (!name || !handle || seen[handle]) continue;
        if (needle && name.toLowerCase().indexOf(needle) === -1) continue;
        seen[handle] = true;
        out.push({ handle: handle, name: name, imageUrl: images[j] || "" });
      }
    }
    return out;
  }

  // --- Markup builders ----------------------------------------------------

  function buildResultsMarkup(cfg, term, isArabic, t, hits, vendors, collections) {
    var hasAnything = hits.length || vendors.length || collections.length;

    if (!hasAnything) {
      return (
        '<p class="text-center p-break-words py-6 text-small color-text-[rgba(var(--color-text),0.6)]">' +
        escapeHtml(t.noResults) +
        ' "' +
        escapeHtml(term) +
        '"</p>'
      );
    }

    var leftCol = renderRefineSection(vendors, collections, t, isArabic);
    var rightCol = renderProductsSection(cfg, hits, isArabic, t);
    var viewAllText = t.viewAll.replace("{term}", term);

    // Sidebar is full-width while stacked on mobile (flex-col) and only
    // becomes a fixed 220px column once it sits side-by-side with the
    // product grid at lg (flex-row) -- a fixed width on a stacked mobile
    // block was the cause of the cropped/narrow layout.
    return (
      '<div class="flex flex-col lg:flex-row gap-5 md:gap-6 md:px-5">' +
      (leftCol ? '<div class="ts-sidebar">' + leftCol + "</div>" : "") +
      '<div class="flex-1 min-w-0">' +
      rightCol +
      "</div>" +
      "</div>" +
      '<div class="sticky bottom-0 z-10 pt-4 mt-5 bg-gradient color-bg-gradient-[var(--gradient-background)]">' +
      '<div class="view-all-button border-solid text-center w-full border-t color-bg-[rgba(var(--color-heading),0.06)]">' +
      '<a href="' +
      localizedUrl("/search?q=" + encodeURIComponent(term) + "&type=product", isArabic) +
      '" class="inline-block w-full py-3 link-hover">' +
      escapeHtml(viewAllText) +
      "</a></div></div>"
    );
  }

  function renderSectionHeading(text) {
    return (
      '<p class="suggest-search-heading h5 pb-2.5 md:pb-4 font-semibold empty:hidden">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  // Brands/collections: a single horizontally-scrolling row of tiles below
  // lg (mobile/tablet), switching to a 2-column grid at lg where the
  // sidebar has its own fixed width to grid against. All of the responsive
  // switching (width/display/overflow) lives in plain injected CSS
  // (.ts-refine-*, see injectTypesenseStyles) rather than Tailwind utility
  // classes -- those classes don't already exist anywhere else in the
  // theme's Liquid files, so there's no guarantee Tailwind's build actually
  // compiled them in. Plain injected CSS always works regardless.
  function renderRefineSection(vendors, collections, t, isArabic) {
    var out = "";

    if (vendors.length) {
      out +=
        "<div>" +
        renderSectionHeading(t.brands) +
        '<div class="ts-refine-row">' +
        vendors
          .map(function (v) {
            return renderBrandTile(
                localizedUrl("/collections/vendors?q=" + encodeURIComponent(v.urlName), isArabic),
                v.logoUrl,
                v.name
            );
          })
          .join("") +
        "</div></div>";
    }

    if (collections.length) {
      out +=
        '<div class="' +
        (vendors.length ? "pt-5" : "") +
        '">' +
        renderSectionHeading(t.collections) +
        '<div class="ts-refine-row">' +
        collections
          .map(function (c) {
            return renderCollectionTile(
                localizedUrl("/collections/" + encodeURIComponent(c.handle), isArabic),
                c.imageUrl,
                c.name
            );
          })
          .join("") +
        "</div></div>";
    }

    return out;
  }

  // Brand tile: logo only (bordered square) when there is one; otherwise the
  // name fills the same bordered square -- no separate caption underneath
  // either way.
  function renderBrandTile(url, logoUrl, name) {

    var image = logoUrl || DEFAULT_IMAGE;

    return (
        '<a href="' +
        url +
        '" class="ts-refine-tile ts-brand-tile flex flex-col items-center text-center">' +

            '<span class="ts-refine-media flex items-center justify-center rounded-corners overflow-hidden border border-solid bg-[rgba(0,0,0,0.03)] p-2">' +
                '<img src="' +
                escapeHtml(image) +
                '" alt="' +
                escapeHtml(name) +
                '" loading="lazy" class="w-full h-full object-contain">' +
            "</span>" +

            '<span class="ts-refine-name h7 capitalize p-break-words text-center line-clamp-2 text-x-small color-text-[rgba(var(--color-text-header),0.6)] mt-1">' +
                escapeHtml(name) +
            "</span>" +

        "</a>"
    );
}

  // Collection tile keeps its image-plus-caption shape (unlike the brand
  // tile), just made carousel-friendly on mobile via the same ts-refine-tile
  // mechanics, with the wide (1/0.75) media ratio instead of square.
  function renderCollectionTile(url, imageUrl, name) {

        var image = imageUrl || DEFAULT_IMAGE;

        return (
            '<a href="' +
            url +
            '" class="ts-refine-tile ts-collection-tile flex flex-col items-center text-center">' +

                '<span class="ts-refine-media ts-refine-media-wide block rounded-corners overflow-hidden border border-solid bg-[rgba(0,0,0,0.03)]">' +

                    '<img src="' +
                    escapeHtml(image) +
                    '" alt="' +
                    escapeHtml(name) +
                    '" loading="lazy" class="w-full h-full object-contain">' +

                "</span>" +

                '<span class="ts-refine-name h7 capitalize p-break-words text-center line-clamp-2 text-x-small color-text-[rgba(var(--color-text-header),0.6)] mt-1">' +
                    escapeHtml(name) +
                "</span>" +

            "</a>"
        );
    }

  function renderProductsSection(cfg, hits, isArabic, t) {
    if (!hits.length) return "";
    return (
      renderSectionHeading(t.products) +
      '<ul role="listbox" class="predictive-result-list grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">' +
      hits
        .map(function (hit) {
          return renderProductItem(cfg, hit, isArabic);
        })
        .join("") +
      "</ul>"
    );
  }

  // --- Badges -------------------------------------------------------------
  // offer_badge is already the display-ready string from the sync job
  // ("B1G1" / "B2G1" / "-NN%"), matching offer-type-badge-card.liquid's own
  // parsing logic, so it's just rendered here, not re-parsed.

  function renderOfferBadge(badgeText) {
    if (!badgeText) return "";
    return (
      '<div class="top-left-container label-container absolute flex gap-1 pointer-events-none top-1.5 flex-col left-1.5 right-1.5 text-left">' +
      '<div class="x-badge-sale-label x-badge-container pointer-events-none ltr" style="--rounded-corners: 4px;">' +
      '<div class="x-badge-content x-badge-text h7 px-2 py-1 text-left ltr select-none inline-flex justify-center items-center opacity-100 rounded-corners gap-2" ' +
      'style="font-size: var(--h7-font-size);background-color:#f2eb41;border-color:#f2eb41;color:#1d28aa">' +
      '<span class="leading-normal w-fit p-break-words">' +
      escapeHtml(badgeText) +
      "</span></div></div></div>"
    );
  }

  function renderPrescribedBadge(isPrescribed) {
    if (!isPrescribed) return "";
    return '<div class="ts-prescribed-bdg" aria-label="Prescription required">PRESCRIBED</div>';
  }

  // Injected once (guarded). Plain CSS, not Tailwind utility classes --
  // Tailwind is compiled by scanning the theme's actual .liquid files for
  // class-name strings, so utility combinations that only ever appear inside
  // this JS file (arbitrary values, scroll-snap, responsive grid switches)
  // have no guarantee of being in the compiled theme.css. Writing real CSS
  // and injecting it directly sidesteps that entirely.
  function injectPrescribedBadgeStyle() {
    if (document.getElementById("ts-prescribed-bdg-style")) return;
    var style = document.createElement("style");
    style.id = "ts-prescribed-bdg-style";
    style.textContent =
      ".ts-prescribed-bdg{display:block;margin-top:4px;background:#5A97FA;color:#fff;padding:4px 10px;" +
      "font-size:11px;font-weight:600;border-radius:999px;letter-spacing:.3px;width:fit-content;line-height:1;}" +
      // Sidebar: full width while stacked (mobile), fixed column once
      // side-by-side with the product grid (desktop).
      ".ts-sidebar{width:100%;}" +
      // Brands/collections row: horizontal scroll on mobile, 2-col grid on
      // desktop.
      ".ts-refine-row{display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;" +
      "-webkit-overflow-scrolling:touch;scroll-snap-type:x mandatory;}" +
      ".ts-refine-tile{flex:0 0 80px;scroll-snap-align:start;}" +
      ".ts-refine-tile.ts-refine-tile-wide{flex-basis:96px;}" +
      ".ts-refine-media{width:100%;aspect-ratio:1/1;}" +
      ".ts-refine-media.ts-refine-media-wide{aspect-ratio:1/0.75;}" +
      "@media (min-width:1024px){" +
      ".ts-sidebar{width:320px;flex:0 0 320px;}" +
      ".ts-refine-row{display:grid;grid-template-columns:repeat(3,1fr);overflow:visible;padding-bottom:0;}" +
      ".ts-refine-tile{flex:initial;}" +
      "}";
    document.head.appendChild(style);
  }

  /*function renderProductItem(cfg, hit, isArabic) {
    var doc = hit.document;
    var highlightField = isArabic ? "name_ar" : "name_en";

    var nameHtml =
      hit.highlight && hit.highlight[highlightField] && hit.highlight[highlightField].value
        ? hit.highlight[highlightField].value
        : escapeHtml(isArabic ? doc.name_ar : doc.name_en);

    var productName = isArabic ? doc.name_ar : doc.name_en;
    var vendorName = isArabic ? doc.vendor_name_ar : doc.vendor_name_en;
    var url = "/products/" + encodeURIComponent(doc.handle);

    var addToCart = "";
    if (doc.available && doc.variant_id) {
      addToCart =
        '<button type="button" ' +
        'class="button button-primary w-full text-small py-1.5 px-2 mt-auto rounded-corners-sm" ' +
        'data-variant-id="' +
        escapeHtml(doc.variant_id) +
        '" ' +
        "onclick=\"(function(btn){" +
        "btn.disabled=true;" +
        "fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:" +
        Number(doc.variant_id) +
        ",quantity:1})})" +
        ".then(function(r){return r.json();})" +
        ".then(function(){" +
        "btn.textContent='Added!';" +
        "document.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true}));" +
        "setTimeout(function(){btn.textContent='Add to cart';btn.disabled=false;},1500);" +
        "})" +
        ".catch(function(){btn.disabled=false;});" +
        '})(this)">' +
        "Add to cart</button>";
    } else {
      addToCart =
        '<a href="' +
        url +
        '" class="btn btn--secondary w-full text-small py-1.5 px-2 mt-auto rounded-corners-sm text-center opacity-60">' +
        "Sold out</a>";
    }

    return (
      '<li role="option" aria-selected="false" class="product-card__wrapper p-2 color-scheme-theme rounded-corners text-left overflow-hidden flex flex-col gap-2 relative border border-solid">' +
      '<a href="' +
      url +
      '" class="w-full block">' +
      '<div class="slide-item w-full" style="aspect-ratio: 1 / 0.75;">' +
      (doc.image_url
        ? '<img class="product-card__image w-full object-contain h-full [image-rendering:auto] max-w-full" src="' +
          escapeHtml(doc.image_url) +
          '" alt="' +
          escapeHtml(productName) +
          '" width="300" height="225" loading="lazy">'
        : '<div class="w-full h-full"></div>') +
      "</div></a>" +
      '<div class="flex-1 px-1 pb-2">' +
      (vendorName
        ? '<div class="mb-1 p-break-words tracking-widest text-x-small color-text-[rgba(var(--color-text-header),0.6)]">' +
          escapeHtml(vendorName) +
          "</div>"
        : "") +
      '<a href="' +
      url +
      '" class="mb-1 title-link block">' +
      '<h7 class="h7 capitalize p-break-words">' +
      nameHtml +
      "</h7></a>" +
      renderOfferBadge(doc.offer_badge) +
      renderPrescribedBadge(doc.is_prescribed) +
      renderProductPrice(cfg, doc) +
      "</div>" +
      addToCart +
      "</li>"
    );
  }*/
 function localizedUrl(path, isArabic) {
    if (!isArabic) return path;

    // avoid double /ar
    if (path.indexOf("/ar/") === 0) return path;

    return "/ar" + path;
 }

 function renderProductItem(cfg, hit, isArabic) {

    var doc = hit.document;
    var highlightField = isArabic ? "name_ar" : "name_en";

    var nameHtml =
        hit.highlight &&
        hit.highlight[highlightField] &&
        hit.highlight[highlightField].value
        ? hit.highlight[highlightField].value
        : escapeHtml(isArabic ? doc.name_ar : doc.name_en);

    var productName = isArabic ? doc.name_ar : doc.name_en;
    var vendorName = isArabic ? doc.vendor_name_ar : doc.vendor_name_en;
    var url = localizedUrl(
        "/products/" + encodeURIComponent(doc.handle),
        isArabic
    );

    var txtAdd = isArabic ? "أضف إلى السلة" : "Add to cart";
    var txtAdded = isArabic ? "تمت الإضافة" : "Added!";
    var txtSold = isArabic ? "نفدت الكمية" : "Sold out";

    var addToCart = "";

    if (doc.available && doc.variant_id) {

        addToCart =
        '<button type="button" ' +
        'class="button button-primary w-full text-small py-1.5 px-2 mt-auto rounded-corners-sm" ' +
        'data-variant-id="' +
        escapeHtml(doc.variant_id) +
        '" ' +
        "onclick=\"(function(btn){" +
        "btn.disabled=true;" +
        "fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:" +
        Number(doc.variant_id) +
        ",quantity:1})})" +
        ".then(function(r){return r.json();})" +
        ".then(function(){" +
        "btn.textContent='" + txtAdded + "';" +
        "document.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true}));" +
        "setTimeout(function(){" +
        "btn.textContent='" + txtAdd + "';" +
        "btn.disabled=false;" +
        "},1500);" +
        "})" +
        ".catch(function(){btn.disabled=false;});" +
        '})(this)">' +
        txtAdd +
        "</button>";

    } else {

        addToCart =
        '<a href="' +
        url +
        '" class="btn btn--secondary w-full text-small py-1.5 px-2 mt-auto rounded-corners-sm text-center opacity-60">' +
        txtSold +
        "</a>";

    }

    return (
        '<li role="option" aria-selected="false" class="product-card__wrapper p-2 color-scheme-theme rounded-corners text-left overflow-hidden flex flex-col gap-2 relative border border-solid">' +

        '<a href="' +
        url +
        '" class="w-full block">' +

            '<div class="slide-item w-full" style="aspect-ratio:1 / 0.75;">' +

            (doc.image_url
                ? '<img class="product-card__image w-full object-contain h-full [image-rendering:auto] max-w-full" src="' +
                escapeHtml(doc.image_url) +
                '" alt="' +
                escapeHtml(productName) +
                '" width="300" height="225" loading="lazy">'
                : '<div class="w-full h-full"></div>') +

            "</div>" +

        "</a>" +

        '<div class="flex-1 px-1 pb-2">' +

            (vendorName
            ? '<div class="mb-1 p-break-words tracking-widest text-x-small color-text-[rgba(var(--color-text-header),0.6)]">' +
                escapeHtml(vendorName) +
                "</div>"
            : "") +

            '<a href="' +
            url +
            '" class="mb-1 title-link block">' +

            '<h7 class="h7 capitalize p-break-words">' +
            nameHtml +
            "</h7>" +

            "</a>" +

            //renderOfferBadge(doc.offer_badge) +
            renderOfferBadge(doc.offer_badge || computeCompareAtBadge(doc)) +
            renderPrescribedBadge(doc.is_prescribed) +
            renderProductPrice(cfg, doc, isArabic) +

        "</div>" +

        addToCart +

        "</li>"
    );
  }

  /*function renderProductPrice(cfg, doc) {

    var price = Number(doc.price || 0);
    var salePrice = Number(doc.sale_price || 0);

    var onSale =
        doc.on_sale === true ||
        doc.on_sale === "true" ||
        doc.on_sale === 1 ||
        doc.on_sale === "1";


    if (onSale && salePrice > 0 && salePrice < price) {

        return (
            '<div class="h6 mt-1 flex items-center gap-2">' +

                '<span class="price">' +
                    formatMoney(cfg, salePrice) +
                '</span>' +

                '<span class="text-[80%] compare-at-price line-through">' +
                    formatMoney(cfg, price) +
                '</span>' +

            '</div>'
        );

    }

    return (
        '<div class="main-price mt-1">' +
            formatMoney(cfg, price) +
        '</div>'
    );
  }*/
 function renderProductPrice(cfg, doc, isArabic) {
  var price = Number(doc.price || 0);
  var salePrice = Number(doc.sale_price || 0);
  var compareAtPrice = Number(doc.compare_at_price || 0);

  if (salePrice > 0 && salePrice < price) {
    return renderPriceMarkup(cfg, salePrice, price, false, doc, isArabic);
  }

  if (compareAtPrice > price) {
    return renderPriceMarkup(cfg, price, compareAtPrice, true, doc, isArabic);
  }

  return '<div class="main-price mt-1">' + formatMoney(cfg, price) + "</div>";
}

function renderPriceMarkup(cfg, currentPrice, wasPrice, showMegaSaleBadge, doc, isArabic) {
  var html =
    '<div class="h6 mt-1 flex items-center gap-2">' +
      '<span class="price">' + formatMoney(cfg, currentPrice) + "</span>" +
      '<span class="text-[80%] compare-at-price line-through">' +
        formatMoney(cfg, wasPrice) +
      "</span>" +
    "</div>";

  if (showMegaSaleBadge) {
    html += renderMegaSaleBadge(doc, isArabic);
  }
  return html;
}

function renderMegaSaleBadge(doc, isArabic) {
  var badgeText = isArabic ? "تخفيضات كبرى" : "MEGA SALE";
  var uniqueId = (doc && doc.id ? doc.id : Math.random().toString(36).slice(2)) + "-ts-cart";

  return (
    '<div class="mega-sale-badge-cart" aria-label="' + escapeHtml(badgeText) + '">' +
    '<svg width="100" height="32" viewBox="0 0 100 32" xmlns="http://www.w3.org/2000/svg" role="img" preserveAspectRatio="none">' +
      "<defs>" +
        '<linearGradient id="fireGrad-' + uniqueId + '" x1="0%" y1="100%" x2="0%" y2="0%">' +
          '<stop offset="0%" stop-color="#FF8C00">' +
            '<animate attributeName="stop-color" values="#FF8C00;#FF6A00;#FF8C00" dur="3s" repeatCount="indefinite"/>' +
          "</stop>" +
          '<stop offset="50%" stop-color="#FF4500">' +
            '<animate attributeName="stop-color" values="#FF4500;#FF2200;#FF4500" dur="2s" repeatCount="indefinite"/>' +
          "</stop>" +
          '<stop offset="100%" stop-color="#D30F2F">' +
            '<animate attributeName="stop-color" values="#D30F2F;#FF3D3D;#D30F2F" dur="4s" repeatCount="indefinite"/>' +
          "</stop>" +
        "</linearGradient>" +
        '<filter id="glow-' + uniqueId + '">' +
          '<feGaussianBlur stdDeviation="2.5" result="blur"/>' +
          "<feMerge>" +
            '<feMergeNode in="blur"/>' +
            '<feMergeNode in="SourceGraphic"/>' +
          "</feMerge>" +
        "</filter>" +
      "</defs>" +

      '<rect width="100" height="32" rx="6" fill="url(#fireGrad-' + uniqueId + ')"/>' +

      '<path d="M8 32C12 12 20 16 18 2C24 8 32 6 30 32H8Z" fill="#FFD700" opacity="0.35" filter="url(#glow-' + uniqueId + ')">' +
        '<animateTransform attributeName="transform" type="translate" values="0 1;0 -2;0 1" dur="1.4s" repeatCount="indefinite"/>' +
      "</path>" +

      '<path d="M15 32C18 16 22 16 21 6C25 10 28 9 27 32H15Z" fill="#FFF3B0" opacity="0.65">' +
        '<animateTransform attributeName="transform" type="translate" values="0 1;0 -1;0 1" dur="1s" repeatCount="indefinite"/>' +
      "</path>" +

      '<path d="M60 32C66 10 76 14 72 0C80 7 88 5 86 32H60Z" fill="#FFD700" opacity="0.30" filter="url(#glow-' + uniqueId + ')">' +
        '<animateTransform attributeName="transform" type="translate" values="0 1;0 -3;0 1" dur="1.6s" repeatCount="indefinite"/>' +
      "</path>" +

      '<path d="M68 32C72 15 76 15 75 5C80 10 83 8 82 32H68Z" fill="#FFF3B0" opacity="0.55">' +
        '<animateTransform attributeName="transform" type="translate" values="0 1;0 -2;0 1" dur="1.2s" repeatCount="indefinite"/>' +
      "</path>" +

      '<circle cx="24" cy="24" r="1.2" fill="#FFF3B0">' +
        '<animate attributeName="cy" values="24;8;24" dur="2.5s" repeatCount="indefinite"/>' +
        '<animate attributeName="opacity" values="0;1;0" dur="2.5s" repeatCount="indefinite"/>' +
      "</circle>" +

      '<circle cx="74" cy="26" r="1" fill="#FFF3B0">' +
        '<animate attributeName="cy" values="26;10;26" dur="3s" repeatCount="indefinite"/>' +
        '<animate attributeName="opacity" values="0;0.8;0" dur="3s" repeatCount="indefinite"/>' +
      "</circle>" +

      '<text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" ' +
      'font-family="\'Outfit\', sans-serif" font-weight="900" font-size="' + (isArabic ? "11" : "12") + '" fill="#FFFFFF" ' +
      'letter-spacing="0.05em" style="text-transform:uppercase;text-shadow:1px 1px 4px rgba(0,0,0,0.65);">' +
        escapeHtml(badgeText) +
      "</text>" +
    "</svg>" +
    "</div>"
  );
}

// Returns a percentage-off badge string (e.g. "-25%") when compare_at_price
// implies a discount, so it can be passed straight into renderOfferBadge.
function computeCompareAtBadge(doc) {
  var price = Number(doc.price || 0);
  var compareAtPrice = Number(doc.compare_at_price || 0);

  if (compareAtPrice > price && compareAtPrice > 0) {
    var pct = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
    return "-" + pct + "%";
  }
  return "";
}

  function formatMoney(cfg, amount) {
    if (window.Shopify && typeof Shopify.formatMoney === "function" && cfg.moneyFormat) {
      return Shopify.formatMoney(Math.round(amount * 100), cfg.moneyFormat);
    }
    return amount.toFixed(2) + " " + (cfg.currency || "");
  }

  function escapeHtml(str) {
    var div = document.createElement("div");
    div.textContent = str == null ? "" : String(str);
    return div.innerHTML;
  }
})();
